import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { TableProps } from '@qwealth/pdf-support';
import { toCurrency } from '@qwealth/qcore';
import { NetWorthReportData } from '@qwealth/qdata';

import { COLORS } from '../../constant';
import commonStyles from '../../commonStyles';
import { AggregatedQScoreData } from '../interfaces/AggregatedQScoreData';

const styles = StyleSheet.create({
  MainText1: {
    fontSize: 10
  },
  BoldFont: {
    fontWeight: 'bold'
  },
  Row: {
    display: 'flex',
    flexDirection: 'row'
  },
  Col: {
    flex: 1,
    fontSize: 8
  }
});

const createLegacyValueCols = (): TableProps<NetWorthReportData>['cols'] => [
  { title: 'Year', val: ({ year }) => year },
  {
    title: 'Total lifestyle\nassets',
    val: ({ summaries }) =>
      toCurrency(
        summaries.find((summary) => summary.item === 'Total lifestyle assets')?.client,
        true
      )
  },
  {
    title: 'Total rental\nproperties',
    val: ({ summaries }) =>
      toCurrency(
        summaries.find((summary) => summary.item === 'Total rental properties')?.client,
        true
      )
  },
  {
    title: 'Total\naccounts',
    val: ({ summaries }) =>
      toCurrency(summaries.find((summary) => summary.item === 'Total accounts')?.client, true)
  },
  {
    title: 'Total\ncorporations',
    val: ({ summaries }) =>
      toCurrency(summaries.find((summary) => summary.item === 'Total corporations')?.client, true)
  },
  {
    title: 'Total\nliabilities',
    val: ({ summaries }) =>
      toCurrency(summaries.find((summary) => summary.item === 'Total liabilities')?.client, true)
  },
  {
    title: 'Total\ncash flow',
    val: ({ summaries }) =>
      toCurrency(summaries.find((summary) => summary.item === 'Total cash flow')?.client, true)
  },
  {
    title: 'Total\nNet Worth',
    val: ({ summaries }) =>
      toCurrency(summaries.find((summary) => summary.item === 'TOTAL NET WORTH')?.client, true)
  }
];

const ReportLegacyValues1 = ({ data }: { data: AggregatedQScoreData }) => {
  const cols = createLegacyValueCols();

  return (
    <Page size="A4" orientation="landscape" style={[commonStyles.Root, { paddingBottom: 40 }]}>
      <View style={[commonStyles.Header, { marginBottom: 30 }]} fixed>
        <Text style={commonStyles.HeaderTitle}>YEAR END AND VALUES OF EACH ACCOUNT</Text>
        <Text style={commonStyles.PageNumber} render={({ pageNumber, totalPages }) => pageNumber} />
      </View>

      <View
        style={{
          paddingHorizontal: 30
        }}
      >
        <Text style={[styles.MainText1, { marginBottom: 10 }]}>
          This report tells you what the Year End Values of each of your investment accounts MIGHT
          be. This is only <Text style={styles.BoldFont}>ONE</Text> simulation. It is the exact
          middle simulation meaning half of the simulations had your values above these numbers and
          half had them below. It is a valuable benchmark to see to what extent you are tracking
          either above or below the middle results. Every time you rerun the plan; the numbers will
          change, based on the order of the returns. You should however see patterns emerge. Most
          importantly, you want to see your individual accounts performing in alignment with the
          benchmarks of that allocation. If over an extended period (one year or more) that is{' '}
          <Text style={styles.BoldFont}>NOT THE CASE</Text>, you may want to consider changing this
          investment approach, as you potentially may not reach the stated Desired Outcomes in your
          plan.
        </Text>
        <Text style={{ color: COLORS.PRIMARY, fontWeight: 'semibold', marginBottom: 20 }}>
          Legacy Value
        </Text>
        <View style={styles.Row}>
          {cols &&
            cols.map((col, index) => (
              <View key={`${2021 + index}`} style={styles.Col}>
                <View
                  style={{ height: 26, borderBottomWidth: 2, borderBottomColor: COLORS.GRAY1 }}
                  fixed
                >
                  <Text
                    style={{
                      color: COLORS.PRIMARY,
                      textTransform: 'uppercase',
                      fontWeight: 'semibold'
                    }}
                  >
                    {col.title}
                  </Text>
                </View>
                <View>
                  {data.netWorthReport &&
                    data.netWorthReport.map((report, index) => (
                      <View
                        key={`report-${index}`}
                        style={{
                          height: 16,
                          borderBottomWidth: 2,
                          borderBottomColor: COLORS.GRAY1,
                          paddingTop: 4
                        }}
                      >
                        <Text>{col.val(report)}</Text>
                      </View>
                    ))}
                </View>
              </View>
            ))}
        </View>
      </View>
    </Page>
  );
};

export default ReportLegacyValues1;
